// B2-GATES: the in-memory API's board gates (services/api/src/domain/boards/gates.ts) with the
// real routes' rules: a person sets the gates of a seat it holds, an editor the board default, an
// admin the ceiling (no `trust` on the board, a `trust` row then reads as `hold`); a trust under
// the ceiling is refused. Holds are not simulated (no DE moves here): a task has no hold.
// No demo rows: every board starts without gates.
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem, validation } from "./fake";
import { NO_EXECUTION } from "./fake-approval-execution";
import { type FakeDe, uid } from "./fake-data";

type Gates = OperationOutput<"boards.gates">;
type Row = Gates["gates"][number];
type Put = OperationInput<"boards.putGate">["body"];

type Stored = Omit<Row, "seat_title" | "holder_name" | "effective_mode" | "mine" | "can_edit">;

export type GateDeps = {
  boardsOf: (companyId: string) => { id: string; status_set: { key: string }[] }[];
  /** The seats `p` holds in its company (a DE's supervisor seat is its person's). */
  mySeatIds: (p: Principal) => string[];
  /** A seat's title and holder, for the list. */
  seatOf: (companyId: string, seatId: string) => { title: string; holder: string | null } | null;
};

const admin = (p: Principal) => p.roles.some((r) => r === "owner" || r === "admin");
const editor = (p: Principal) => p.roles.some((r) => r !== "client_viewer");

type Approval = OperationOutput<"my.approvals">["data"][number];
type TaskLite = {
  id: string;
  board_id: string;
  number: number;
  title: string;
  status: string;
  version: number;
  due_at: string | null;
  completed_at: string | null;
  assignee_seat_ids: string[];
};

/**
 * The demo's one batch review (local seed only): a DE asks to move the biggest open group of
 * tasks on the company's first board with one to the next status, filed to its supervisor.
 * Real tasks of the work twin, so a decided row reads like the server's; null when none fit.
 */
export function seedBatchReview(
  company: { id: string },
  des: FakeDe[],
  boards: { id: string; status_set: { key: string; name: string; done?: boolean }[] }[],
  tasks: TaskLite[],
  now: Date,
): Approval | null {
  for (const board of boards) {
    const open = tasks.filter((t) => t.board_id === board.id && !t.completed_at);
    const groups = new Map<string, TaskLite[]>();
    for (const t of open) groups.set(t.status, [...(groups.get(t.status) ?? []), t]);
    const [from, rows] = [...groups.entries()].sort((a, b) => b[1].length - a[1].length)[0] ?? [];
    const at = board.status_set.findIndex((s) => s.key === from);
    const to = board.status_set[at + 1];
    if (!rows || rows.length < 2 || !to) continue;
    const items = rows.slice(0, 6);
    const de =
      des.find((d) => items.some((t) => t.assignee_seat_ids.includes(d.seat_id))) ?? des[0];
    if (!de) return null;
    const t = new Date(now.getTime() - 50 * 60_000).toISOString();
    const drop = board.status_set.find((s) => s.done && s.key !== to.key)?.key ?? null;
    return {
      company_id: company.id,
      id: uid(0xb2a8, 1),
      kind: "task_batch",
      subject_kind: "board",
      subject_id: board.id,
      requested_by_seat_id: de.seat_id,
      requested_by_membership_id: de.membership_id,
      approver_seat_id: de.supervisor.seat_id,
      run_id: null,
      proposal: {
        title: `Move ${items.length} tasks to ${to.name}`,
        summary: "Keep, edit, send back or drop each row; one decision applies them all.",
        change: {
          board_id: board.id,
          to_status: to.key,
          drop_status: drop,
          items: items.map((x) => ({
            task_id: x.id,
            version: x.version,
            number: x.number,
            title: x.title,
            from_status: x.status,
            due_at: x.due_at,
          })),
          precondition: { kind: "affected_set", digest: "0".repeat(64), count: items.length },
        },
      },
      state: "pending",
      approved_payload: null,
      decided_at: null,
      decided_by_membership_id: null,
      reason: null,
      thread_id: null,
      expires_at: new Date(now.getTime() + 2 * 86_400_000).toISOString(),
      executed_at: null,
      ...NO_EXECUTION,
      actor_kind: "digital_employee",
      actor_membership_id: de.membership_id,
      created_at: t,
      updated_at: t,
    };
  }
  return null;
}

/**
 * Applies a decided batch review to the work twin's rows (the server's `applyTaskBatch`): keep and
 * edit move to `to_status`, drop to `drop_status` (or stays), send back stays; a task changed
 * since it was filed (version) is skipped.
 */
export function applyBatchReview(
  change: Record<string, unknown> | undefined,
  taskOf: (
    id: string,
  ) => { status: string; title: string; due_at: string | null; version: number } | null | undefined,
) {
  const b = change as
    | {
        to_status: string;
        drop_status: string | null;
        items: {
          task_id: string;
          version: number;
          decision?: string;
          edit?: { title?: string; due_at?: string | null };
        }[];
      }
    | undefined;
  for (const i of b?.items ?? []) {
    const t = taskOf(i.task_id);
    if (!t || t.version !== i.version) continue;
    const d = i.decision ?? "keep";
    if (d === "send_back" || (d === "drop" && !b?.drop_status)) continue;
    if (d === "edit" && i.edit) {
      if (i.edit.title) t.title = i.edit.title;
      if (i.edit.due_at !== undefined) t.due_at = i.edit.due_at;
    }
    t.status = d === "drop" ? (b?.drop_status as string) : (b?.to_status as string);
    t.version += 1;
  }
}

export function createGateHandlers(d: GateDeps): Partial<Record<RouteId, Handler>> {
  let seq = 0;
  const gates = new Map<string, Stored[]>();
  const noTrust = new Set<string>();
  const boardOf = (co: string, id: string | undefined) =>
    d.boardsOf(co).find((b) => b.id === id) ?? null;
  const listOf = (b: string) => {
    const l = gates.get(b) ?? [];
    gates.set(b, l);
    return l;
  };
  const mayWrite = (p: Principal, seat: string | null) =>
    seat === null ? editor(p) : d.mySeatIds(p).includes(seat) || admin(p);

  function view(p: Principal, boardId: string): Gates {
    const mine = d.mySeatIds(p);
    const capped = noTrust.has(boardId);
    return {
      board_id: boardId,
      no_trust: capped,
      can_set_ceiling: admin(p),
      can_edit_default: editor(p),
      my_seat_ids: mine,
      gates: listOf(boardId).map((g) => {
        const seat = g.seat_id ? d.seatOf(p.company.id, g.seat_id) : null;
        return {
          ...g,
          seat_title: seat?.title ?? null,
          holder_name: seat?.holder ?? null,
          effective_mode: capped && g.mode === "trust" ? "hold" : g.mode,
          mine: g.seat_id !== null && mine.includes(g.seat_id),
          can_edit: mayWrite(p, g.seat_id),
        };
      }),
    };
  }

  return {
    "boards.gates": ({ params, principal: p }) => {
      if (!p || !boardOf(p.company.id, params.id)) return notFound();
      return json(view(p, params.id as string));
    },
    "boards.putGate": ({ params, body, principal: p }) => {
      const board = p ? boardOf(p.company.id, params.id) : null;
      if (!p || !board) return notFound();
      const b = body as Put;
      if (!mayWrite(p, b.seat_id))
        return problem(403, "forbidden", "Forbidden", "you may set only your own gates");
      const keys = board.status_set.map((s) => s.key);
      if (!keys.includes(b.to_status) || (b.from_status && !keys.includes(b.from_status)))
        return validation("unknown status on this board");
      if (b.mode === "trust" && noTrust.has(board.id))
        return validation("an admin does not allow trust on this board");
      const list = listOf(board.id);
      const same = list.find(
        (g) =>
          g.seat_id === b.seat_id && g.from_status === b.from_status && g.to_status === b.to_status,
      );
      const hold = b.hold_minutes ?? same?.hold_minutes ?? 1440;
      if (same) Object.assign(same, { mode: b.mode, hold_minutes: hold });
      else
        list.push({
          id: uid(0xb2a7, ++seq),
          seat_id: b.seat_id,
          from_status: b.from_status,
          to_status: b.to_status,
          mode: b.mode,
          hold_minutes: hold,
        });
      return json(view(p, board.id));
    },
    "boards.deleteGate": ({ params, principal: p }) => {
      if (!p || !boardOf(p.company.id, params.id)) return notFound();
      const list = listOf(params.id as string);
      const i = list.findIndex((g) => g.id === params.gate_id);
      const row = list[i];
      if (!row) return notFound();
      if (!mayWrite(p, row.seat_id)) return problem(403, "forbidden", "Forbidden");
      list.splice(i, 1);
      return json(view(p, params.id as string));
    },
    "boards.gateCeiling": ({ params, body, principal: p }) => {
      if (!p || !boardOf(p.company.id, params.id)) return notFound();
      if (!admin(p)) return problem(403, "forbidden", "Forbidden", "only an admin sets this");
      const on = (body as OperationInput<"boards.gateCeiling">["body"]).no_trust;
      if (on) noTrust.add(params.id as string);
      else noTrust.delete(params.id as string);
      return json(view(p, params.id as string));
    },
    // BOARD-GATES: the fake has no person-only columns (column gates are off by default)
    "tasks.reviewGate": ({ principal: p }) => (p ? json({ gate: null }) : notFound()),
    "tasks.review": ({ principal: p }) =>
      p ? problem(409, "conflict", "Conflict", "column gates are not switched on") : notFound(),
    "tasks.hold": ({ principal: p }) => (p ? json({ hold: null }) : notFound()),
    "tasks.pullHold": ({ principal: p }) =>
      p ? problem(409, "conflict", "Conflict", "nothing to pull back") : notFound(),
  };
}
